import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { Suspense } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Label } from './label';

// Lazy load ColorPicker to prevent circular dependencies
const ColorPicker = React.lazy(() => 
  import('./color-picker').then(module => ({ default: module.ColorPicker }))
);

interface GradientPickerProps {
  color1: string;
  color2: string;
  direction: 'horizontal' | 'vertical' | 'diagonal';
  onColor1Change: (color: string) => void;
  onColor2Change: (color: string) => void;
  onDirectionChange: (direction: 'horizontal' | 'vertical' | 'diagonal') => void;
}

export const GradientPicker: React.FC<GradientPickerProps> = ({
  color1,
  color2,
  direction,
  onColor1Change,
  onColor2Change,
  onDirectionChange
}) => {
  useLanguage();
  console.log('🎨 GradientPicker: Rendering with:', { color1, color2, direction });

  // Fallbacks seguros para cores
  const safeColor1 = React.useMemo(() => {
    const safe = color1 && /^#[0-9A-F]{6}$/i.test(color1) ? color1 : '#000000';
    console.log('🎨 GradientPicker: Safe color1:', safe);
    return safe;
  }, [color1]);

  const safeColor2 = React.useMemo(() => {
    const safe = color2 && /^#[0-9A-F]{6}$/i.test(color2) ? color2 : '#ffffff';
    console.log('🎨 GradientPicker: Safe color2:', safe);
    return safe;
  }, [color2]);

  const safeDirection = direction || 'horizontal';

  const getGradientStyle = React.useCallback(() => {
    try {
      const dirMap = {
        horizontal: 'to right',
        vertical: 'to bottom',
        diagonal: 'to bottom right'
      };
      
      const style = {
        background: `linear-gradient(${dirMap[safeDirection]}, ${safeColor1}, ${safeColor2})`
      };
      console.log('🎨 GradientPicker: Generated style:', style);
      return style;
    } catch (error) {
      console.error('🎨 GradientPicker: Error generating gradient style:', error);
      return { background: '#f0f0f0' };
    }
  }, [safeColor1, safeColor2, safeDirection]);

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div className="space-y-2">
        <Label>{translateInterfaceText("Preview do Gradiente")}</Label>
        <div 
          className="h-16 w-full rounded-md border"
          style={getGradientStyle()}
        />
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{translateInterfaceText("Cor 1")}</Label>
          <Suspense 
            fallback={
              <div className="w-full h-10 bg-muted rounded flex items-center justify-center">
                <span className="text-xs text-muted-foreground">{translateInterfaceText("Carregando...")}</span>
              </div>
            }
          >
            <ColorPicker color={safeColor1} onChange={onColor1Change} />
          </Suspense>
        </div>
        <div className="space-y-2">
          <Label>{translateInterfaceText("Cor 2")}</Label>
          <Suspense 
            fallback={
              <div className="w-full h-10 bg-muted rounded flex items-center justify-center">
                <span className="text-xs text-muted-foreground">{translateInterfaceText("Carregando...")}</span>
              </div>
            }
          >
            <ColorPicker color={safeColor2} onChange={onColor2Change} />
          </Suspense>
        </div>
      </div>

      {/* Direction */}
      <div className="space-y-2">
        <Label>{translateInterfaceText("Direção")}</Label>
        <Select value={safeDirection} onValueChange={onDirectionChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="horizontal">{translateInterfaceText("Horizontal (→)")}</SelectItem>
            <SelectItem value="vertical">{translateInterfaceText("Vertical (↓)")}</SelectItem>
            <SelectItem value="diagonal">{translateInterfaceText("Diagonal (↘)")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};